import { z } from 'zod';
/**
 * The numbers on every list screen's scope pills, and on the sidebar's badges.
 *
 * **Why these cannot come from the list endpoints.** Every list in this console
 * pages by keyset, fetching `limit + 1` rows rather than issuing a COUNT (see
 * `pagination.ts`), so no list response knows its own total and none of them
 * ever will. A pill that says "Review queue 42" is therefore a second read by
 * construction.
 *
 * **Why one endpoint rather than one per screen.** The sidebar needs three of
 * these on every screen — the review queue, the open requests, the failing
 * plan runs — so the counts are fetched once for the shell and every screen's
 * pills read the same cached answer. Seven endpoints would mean the shell
 * firing three requests and each screen a fourth.
 *
 * Every field is a `count(*) FILTER` over one table. Deliberately not windowed
 * except where noted: a queue is "how much is waiting", not "how much arrived
 * this month".
 */
/** The window the run counts use. Matches the plan-ops screen's own default. */
export const SCOPE_COUNT_WINDOW_DAYS = 30;
export const adminScopeCountsSchema = z.object({
    users: z.object({
        all: z.number().int(),
        plansFailing: z.number().int(),
        safetyHold: z.number().int(),
        generating: z.number().int(),
        neverOnboarded: z.number().int(),
        deactivated: z.number().int(),
    }),
    foods: z.object({
        catalog: z.number().int(),
        /** Unverified AI estimates: the queue the Food catalog's review view opens on. */
        review: z.number().int(),
    }),
    foodRequests: z.object({
        open: z.number().int(),
        resolved: z.number().int(),
        rejected: z.number().int(),
    }),
    feedback: z.object({
        open: z.number().int(),
        resolved: z.number().int(),
        dismissed: z.number().int(),
    }),
    exercises: z.object({
        active: z.number().int(),
        /** Imported and still inactive — nobody has looked at them yet. */
        review: z.number().int(),
    }),
    /** Exercises equipment scans found missing from the catalog. `open` is the sidebar badge. */
    exerciseRequests: z.object({
        open: z.number().int(),
        resolved: z.number().int(),
        rejected: z.number().int(),
    }),
    /**
     * "From gyms": gym foods and exercises waiting for Steado's decision, and
     * gyms' open reports. Counted in rows, not in the queue's name groups:
     * grouping is the queue's own read.
     */
    gymCatalog: z.object({
        foods: z.number().int(),
        exercises: z.number().int(),
        reports: z.number().int(),
    }),
    billing: z.object({
        subscriptions: z.number().int(),
        events: z.number().int(),
    }),
    activity: z.object({
        console: z.number().int(),
        app: z.number().int(),
    }),
    planRuns: z.object({
        /** Failed or errored in the window. The sidebar badge. */
        failedInWindow: z.number().int(),
        windowDays: z.number().int(),
    }),
    /**
     * The Gyms screen's pills (every gym, setup not finished) and its headline
     * cards (people coached now, trainers active across every gym).
     */
    gyms: z.object({
        all: z.number().int(),
        setupNotDone: z.number().int(),
        coachedNow: z.number().int(),
        trainersActive: z.number().int(),
    }),
});
//# sourceMappingURL=scope-counts.js.map